import React from 'react'
import { LandingPageSection, LandingPageSectionTypes, ResourceDocObject } from '../api/Types'
import LandingPageCaseStudy from './sections/LandingPageCaseStudy'
import LandingPageDesignCarousel from './sections/LandingPageDesignCarousel'
import LandingPageHero from './sections/LandingPageHero'
import LandingPageInfoGraphic from './sections/LandingPageInfoGraphic'
import LandingPageLRContent from './sections/LandingPageLRContent'
import LandingPageMultiTile from './sections/LandingPageMultiTile'
import LandingPageParts from './sections/LandingPageParts'
import LandingPagePromoVideo from './sections/LandingPagePromoVideo'
import LandingPageResource from './sections/LandingPageResource'
import LandingPageStandardGeneral from './sections/LandingPageStandardGeneral'
import LandingPageStandardPartnerLogos from './sections/LandingPageStandardPartnerLogos'
import LandingPageStandardQuotes from './sections/LandingPageStandardQuotes'
import LandingPageStandardRequestInfo from './sections/LandingPageStandardRequestInfo'
import LandingPageStandardStartBuilding from './sections/LandingPageStandardStartBuilding'
import LandingPageTable from './sections/LandingPageTable'
import LandingPageVention101 from './sections/LandingPageVention101'
import LandingPagePureHtml from './sections/LandingPagePureHtml'
import GenericError from '../../common/GenericError'
import LandingPageApplicationSummary from './sections/LandingPageApplicationSummary'
import LandingPageApplicationStats from './sections/LandingPageApplicationStats'
import LandingPageMachineLogicPlans from './sections/LandingPageMachineLogicPlans'
import LandingPageMachineLogicFeatures from './sections/LandingPageMachineLogicFeatures'
import I18nProvider from '../../common/providers/I18nProvider'
import { LocalizedPaths } from '../../../types/Common'
import RequestProvider from '../../common/providers/RequestProvider'
import CurrencyProvider from '../../common/providers/CurrencyProvider'

const allowedSections: LandingPageSectionTypes = {
  caseStudy: LandingPageCaseStudy,
  designCarousel: LandingPageDesignCarousel,
  hero: LandingPageHero,
  applicationSummary: LandingPageApplicationSummary,
  applicationStats: LandingPageApplicationStats,
  infoGraphic: LandingPageInfoGraphic,
  lrContent: LandingPageLRContent,
  multiTile: LandingPageMultiTile,
  parts: LandingPageParts,
  promoVideo: LandingPagePromoVideo,
  resource: LandingPageResource,
  standardGeneral: LandingPageStandardGeneral,
  standardPartnerLogos: LandingPageStandardPartnerLogos,
  standardQuotes: LandingPageStandardQuotes,
  standardRequestInfo: LandingPageStandardRequestInfo,
  standardStartBuilding: LandingPageStandardStartBuilding,
  table: LandingPageTable,
  vention101: LandingPageVention101,
  pureHtml: LandingPagePureHtml,
  machineLogicPlans: LandingPageMachineLogicPlans,
  machineLogicFeatures: LandingPageMachineLogicFeatures,
}

interface Props {
  sections: ReadonlyArray<LandingPageSection>
  resourcesByUuid: Record<string, ResourceDocObject>
  localizedPaths: LocalizedPaths
  isBot: boolean
}

/**
 * Loop over the provided sections.
 * Display each section only if they're typed via the `type` key.
 * If type is not included in the allowed sections, display nothing.
 */
const LandingPageWrapper: React.FunctionComponent<Props> = ({
  sections,
  resourcesByUuid,
  localizedPaths,
  isBot,
}) => {
  if (!Array.isArray(sections)) {
    console.error('Fetched sections are not in the array format\n', sections, '\nAborting...')
    return <GenericError />
  }
  return (
    <I18nProvider localizedPaths={localizedPaths}>
      <RequestProvider isBot={isBot}>
        <CurrencyProvider skipCheckCountryCode={true}>
          <div className='landing-page-wrapper'>
            {sections.map((section: LandingPageSection, idx) => {
              if (section.type) {
                // The Compiler is not capable of inferring this type relationship, but it is safe :D
                const RenderingComponent = allowedSections[section.type]
                if (RenderingComponent) {
                  if (['caseStudy', 'resource', 'hero'].includes(section.type)) {
                    return (
                      <RenderingComponent
                        key={idx}
                        {...section}
                        resourcesByUuid={resourcesByUuid}
                      />
                    )
                  } else {
                    return <RenderingComponent key={idx} {...section} />
                  }
                } else {
                  console.warn(`Type '${section.type}' not allowed for section`, section)
                }
              } else {
                console.warn('No type found for section ', section)
              }
            })}
          </div>
        </CurrencyProvider>
      </RequestProvider>
    </I18nProvider>
  )
}

export default LandingPageWrapper
